import React, {
  MutableRefObject,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { noop } from "lodash/fp";

import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import useMeetingReportUiStore from "@app/analytics/MeetingReport/stores/ui";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";

interface MeetingReportContext {
  mainScrollContainer: MutableRefObject<HTMLDivElement | null>;
  setVideo: (video: HTMLVideoElement | null) => void;
  sidebarScrollContainer: MutableRefObject<HTMLDivElement | null>;
  video: HTMLVideoElement | null;
}

const DEFAULT_MEETING_REPORT_CONTEXT = {
  mainScrollContainer: { current: null },
  setVideo: noop,
  sidebarScrollContainer: { current: null },
  video: null,
};

export const MeetingReportContext = createContext<MeetingReportContext>(
  DEFAULT_MEETING_REPORT_CONTEXT
);

export const useMeetingReportContext = () => useContext(MeetingReportContext);

export const MeetingReportContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);

  const mainScrollContainerRef = useRef(null);
  const sidebarScrollContainerRef = useRef(null);

  const resetEditState = useEditStore((state) => state.reset);
  const resetSearchState = useSearchStore((state) => state.resetState);
  const resetTranscriptState = useTranscriptStore((state) => state.resetState);
  const resetVideoState = useVideoStore((state) => state.resetState);
  const resetUiState = useMeetingReportUiStore((state) => state.resetState);

  useEffect(() => {
    // reset store states on unmount to avoid cross-contamination between reports
    return () => {
      resetEditState();
      resetSearchState();
      resetTranscriptState();
      resetUiState();
      resetVideoState();
    };
  }, []);

  const meetingReportContext: MeetingReportContext = {
    mainScrollContainer: mainScrollContainerRef,
    setVideo,
    sidebarScrollContainer: sidebarScrollContainerRef,
    video,
  };

  return (
    <MeetingReportContext.Provider value={meetingReportContext}>
      {children}
    </MeetingReportContext.Provider>
  );
};
